import React, { useContext, useEffect, useState } from "react";

import { add, format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import PaginatedTable from "../table";

import { DatePicker } from "../form-inputs/inputs";
import Modal from "../modal";
import { buildDefaultShipmentData, filterShipments } from "./lib/shipmentData";
import ShipmentInformation from "../shipment-editor/information/index";
import TaskNav from "../task-nav/task-nav";
import { DEFAULT_FILTER, DEFAULT_PAGE_SIZE, DEFAULT_SORT, getColumns } from "./lib/constants";
import { getStorageValue, setStorageValue } from "../../shared/storage";
import { useInput } from "../../shared/hooks/form-fields";
import { useAlert } from "hooks/useAlert";
import { SettingsContext } from "contexts/settings";
import { ViewOnlyPermissionsContext } from "contexts/view-only-permissions/view-only-permissions";
import { Textarea } from "components/form-inputs/inputs";
import { DATE_FNS_SHORT_DATE_FORMAT } from "../../shared/constants";
import { useNavigate } from "react-router-dom";

const storedFilter = getStorageValue("shipments-filter") || [];
const storedSort = getStorageValue("shipments-sort") || DEFAULT_SORT;
const dateRangeFilter = getStorageValue("shipments-date-range") || {};
const upcsFilter = getStorageValue("filter-upcs-list") || [];

const ShipmentsIndex = () => {
    const storedPageSize = getStorageValue("shipments-page-size") || DEFAULT_PAGE_SIZE;
    let navigate = useNavigate();
    const { toggleAlert } = useAlert();
    const { settings } = useContext(SettingsContext);
    const { handleShipmentCheck, viewOnlyShipment } = useContext(ViewOnlyPermissionsContext);
    const [upcs, setUpcs] = useState(upcsFilter !== "upc" ? upcsFilter : []);
    const [showShipmentModal, setShowShipmentModal] = useState({
        open: false,
        digital: false,
    });

    const startDateMinInput = useInput(dateRangeFilter.from || add(new Date(), { days: -30 }));
    const startDateMaxInput = useInput(dateRangeFilter.to || format(new Date(), DATE_FNS_SHORT_DATE_FORMAT));

    const [pulse, setPulse] = useState(false);

    useEffect(() => {
        handleShipmentCheck(true);
    }, []);

    useEffect(() => {
        if (settings.globalNotifications.messages.length) {
            toggleAlert({ group: "Shipments" });
        }
    }, [settings.globalNotifications.messages.length]);

    const filterData = async (pageNumber, pageSize, sorted, filter) => {
        //remove duplicates
        filter = filter.filter((el) => el.id !== "upcs" && el.id !== "startDate");

        //add date range to filter
        filter.push({ id: "startDate", value: { filterFromDate: startDateMinInput.value, filterToDate: startDateMaxInput.value } });

        //add upcs if available
        if (upcs.length > 0) {
            filter.push({ id: "upcs", values: [upcs] });
        }

        return filterShipments(pageNumber, pageSize, sorted, filter);
    };

    const filterClick = (e) => {
        e.preventDefault();
        setStorageValue("shipments-date-range", { from: startDateMinInput.value, to: startDateMaxInput.value });
        setStorageValue("filter-upcs-list", upcs);
        setPulse(!pulse);
    };

    const rowClick = (e, rowInfo) => {
        navigate("/shipment-editor/" + rowInfo.original.key);
    };

    const refreshShipments = (shipment) => {
        _closeModal();
        navigate(`/shipment-editor/${shipment.key}`);
    };

    const _showShipmentModal = (e) => {
        e.preventDefault();
        setShowShipmentModal({
            open: true,
            digital: false,
        });
    };

    const _showDigitalModal = (e) => {
        e.preventDefault();
        setShowShipmentModal({
            open: true,
            digital: true,
        });
    };

    const _closeModal = () => {
        setShowShipmentModal({
            open: false,
            digital: false,
        });
    };

    const resetDefault = () => {
        setStorageValue("shipments-sort", DEFAULT_SORT);
        setStorageValue("shipments-filter", []);
        setStorageValue("shipments-page-size", DEFAULT_PAGE_SIZE);
        localStorage.removeItem("shipments-date-range");
        setStorageValue("filter-upcs-list", []);
        window.location.reload();
    };

    const shipmentContext = {
        shipmentData: { ...buildDefaultShipmentData(), shipmentType: showShipmentModal.digital ? 1 : 0 },
        updateShipmentData: refreshShipments,
    };

    return (
        <div className="container">
            <TaskNav>
                <div className="button-group">
                    <h1>Shipments</h1>
                    <button
                        className="button -primary -icon -right"
                        onClick={_showDigitalModal}
                        disabled={viewOnlyShipment.createShipment}
                    >
                        Digital Shipment <FontAwesomeIcon icon={faPlusCircle} />
                    </button>
                    <button
                        className="button -primary -icon -right"
                        onClick={_showShipmentModal}
                        disabled={viewOnlyShipment.createShipment}
                    >
                        Shipment <FontAwesomeIcon icon={faPlusCircle} />
                    </button>
                </div>
            </TaskNav>
            <Modal
                show={showShipmentModal.open}
                onClose={_closeModal}
            >
                <ShipmentInformation
                    updateAllowCompleteStatus={() => {}}
                    isDigital={showShipmentModal.digital}
                    onSuccess={refreshShipments}
                    shipmentContext={shipmentContext}
                />
            </Modal>
            <div className="button-group -space-between -flex-end">
                <div className="date-filter">
                    <h3>Filter By Date Range</h3>
                    <DatePicker
                        label="From"
                        name="startDateMin"
                        {...startDateMinInput.bind}
                        enableIcon="true"
                    />
                    <DatePicker
                        label="To"
                        name="startDateMax"
                        {...startDateMaxInput.bind}
                        enableIcon="true"
                    />
                </div>
                <div>
                    <h3>Filter By UPC(s)</h3>
                    <div className="date-filter">
                        <Textarea
                            name="upc"
                            value={upcs}
                            updateOnChange={(id, value) => {
                                setUpcs(value);
                            }}
                        />
                    </div>
                </div>
                <div style={{ marginLeft: "2%" }}>
                    <button
                        className="button"
                        onClick={filterClick}
                    >
                        Filter
                    </button>
                </div>

                <button
                    className="button -small -right"
                    onClick={resetDefault}
                >
                    Reset to Default Sort/Filter
                </button>
            </div>
            <PaginatedTable
                appendSettings={{
                    page: 1,
                    pageSize: storedPageSize,
                    filtered: storedFilter && Array.isArray(storedFilter) ? storedFilter : DEFAULT_FILTER,
                    sorted: storedSort,
                }}
                storagePageSizeValue="shipments-page-size"
                storageSortValue="shipments-sort"
                storageFilterValue="shipments-filter"
                getData={filterData}
                columns={getColumns(settings.locationDataSources)}
                showPagination={true}
                onTaskRowClick={(state, rowInfo) => {
                    const style = {
                        cursor: "pointer",
                    };

                    if (rowInfo && !rowInfo.original.isCompleted) {
                        style.background = "#f9de473e";
                    }

                    if (rowInfo && rowInfo.original.hasMeasuringRedoTask) {
                        style.background = "rgba(255, 204, 204, 0.5)";
                    }

                    return {
                        onDoubleClick: (e) => rowClick(e, rowInfo),
                        style: style,
                    };
                }}
                updatePulse={pulse}
            />
        </div>
    );
};

export default ShipmentsIndex;
